import React, { useContext, useState, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Avatar, Divider,
  Grid, Chip, CircularProgress, Alert, Card, CardContent,
  IconButton, Tooltip, Stack, Switch, FormControlLabel,
} from '@mui/material';
import {
  Edit, Save, Cancel, PhotoCamera, Person, Email, Phone,
  Security, Notifications, Palette, AdminPanelSettings,
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { updateProfile, getProfile } from '../../api/api';
import { useSnackbar } from '../../utils/snackbar';
import { useThemeMode } from '../../context/ThemeContext';
import api from '../../api/api';

const ROLE_CONFIG = {
  admin:    { label: 'Administrator', color: 'secondary', icon: <AdminPanelSettings sx={{ fontSize: 14 }} /> },
  customer: { label: 'Customer',      color: 'primary',   icon: <Person sx={{ fontSize: 14 }} /> },
  rider:    { label: 'Rider',         color: 'success',   icon: <Person sx={{ fontSize: 14 }} /> },
};

export default function Profile() {
  const { user, setUser } = useContext(AuthContext);
  const { showAlert } = useSnackbar();
  const { mode, toggleMode } = useThemeMode();
  const fileRef = useRef();

  const [editing, setEditing]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [pwOpen, setPwOpen]         = useState(false);
  const [savePwBusy, setSavePwBusy] = useState(false);
  const [pwError, setPwError]       = useState('');

  const [form, setForm] = useState({
    name:  user?.name  || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });

  const roleInfo = ROLE_CONFIG[user?.role] || ROLE_CONFIG.admin;

  const handleSave = async () => {
    if (!form.name.trim()) return showAlert('Name is required', 'warning');
    setSaving(true);
    try {
      const { data } = await updateProfile(form);
      const updated = data?.user || data;
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      showAlert('Profile updated successfully', 'success');
      setEditing(false);
    } catch (err) {
      showAlert(err?.response?.data?.message || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' });
    setEditing(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return showAlert('Image must be under 5 MB', 'warning');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = data?.url || data?.imageUrl;
      const { data: updated } = await updateProfile({ image: imageUrl });
      const u = updated?.user || updated;
      setUser(u);
      localStorage.setItem('user', JSON.stringify(u));
      showAlert('Avatar updated', 'success');
    } catch {
      showAlert('Failed to upload avatar', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current) return setPwError('Current password is required');
    if (pwForm.next.length < 6) return setPwError('New password must be at least 6 characters');
    if (pwForm.next !== pwForm.confirm) return setPwError('Passwords do not match');
    setSavePwBusy(true);
    setPwError('');
    try {
      await api.put('/api/me/password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      showAlert('Password changed successfully', 'success');
      setPwForm({ current: '', next: '', confirm: '' });
      setPwOpen(false);
    } catch (err) {
      setPwError(err?.response?.data?.message || 'Failed to change password');
    } finally {
      setSavePwBusy(false);
    }
  };

  const initials = (user?.name || 'A').split(' ').map(s => s[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh', maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="text.primary">My Profile</Typography>
        <Typography variant="body2" color="text.secondary">Manage your account settings and preferences</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left col — Avatar + summary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
              <Avatar
                src={user?.image}
                sx={{
                  width: 96, height: 96, fontSize: 32, fontWeight: 700,
                  background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
                  border: '3px solid',
                  borderColor: 'primary.main',
                }}
              >
                {!user?.image && initials}
              </Avatar>
              <Tooltip title="Change avatar">
                <IconButton
                  size="small"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  sx={{
                    position: 'absolute', bottom: 0, right: -4,
                    bgcolor: 'background.paper', border: '2px solid', borderColor: 'divider',
                    width: 28, height: 28,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {uploading ? <CircularProgress size={12} /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleAvatarUpload} />
            </Box>

            <Typography variant="h6" fontWeight={800} color="text.primary">{user?.name || 'Admin User'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{user?.email}</Typography>

            <Chip
              icon={roleInfo.icon}
              label={roleInfo.label}
              color={roleInfo.color}
              size="small"
              sx={{ fontWeight: 700, mb: 2 }}
            />

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1} sx={{ textAlign: 'left' }}>
              {[
                { icon: <Email sx={{ fontSize: 15 }} />, label: 'Email',   value: user?.email || '—' },
                { icon: <Phone sx={{ fontSize: 15 }} />, label: 'Phone',   value: user?.phone || '—' },
                { icon: <Person sx={{ fontSize: 15 }} />, label: 'Role',   value: roleInfo.label },
              ].map((item) => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: 'text.secondary', flexShrink: 0 }}>{item.icon}</Box>
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" color="text.primary" noWrap fontWeight={600}>{item.value}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Right col — Edit form + settings */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Personal Info */}
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  <Typography variant="h6" fontWeight={700} color="text.primary">Personal Information</Typography>
                </Box>
                {!editing ? (
                  <Button startIcon={<Edit />} size="small" onClick={() => setEditing(true)}>Edit</Button>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={handleCancel} startIcon={<Cancel />}>Cancel</Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSave}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                      sx={{ background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)' }}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </Stack>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Full Name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    fullWidth
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email Address"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    fullWidth
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                    type="email"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    fullWidth
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Role"
                    value={roleInfo.label}
                    fullWidth
                    disabled
                    variant="filled"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Security */}
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Security color="primary" />
                <Typography variant="h6" fontWeight={700} color="text.primary">Security</Typography>
              </Box>

              {!pwOpen ? (
                <Button variant="outlined" startIcon={<Security />} onClick={() => setPwOpen(true)}>
                  Change Password
                </Button>
              ) : (
                <Stack spacing={2}>
                  {pwError && <Alert severity="error" onClose={() => setPwError('')}>{pwError}</Alert>}
                  <TextField
                    label="Current Password"
                    type="password"
                    fullWidth
                    value={pwForm.current}
                    onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    fullWidth
                    value={pwForm.next}
                    onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
                    helperText="Minimum 6 characters"
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    fullWidth
                    value={pwForm.confirm}
                    onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button onClick={() => { setPwOpen(false); setPwForm({ current: '', next: '', confirm: '' }); setPwError(''); }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleChangePassword}
                      disabled={savePwBusy}
                      startIcon={savePwBusy ? <CircularProgress size={14} color="inherit" /> : <Save />}
                      sx={{ background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)' }}
                    >
                      {savePwBusy ? 'Saving…' : 'Update Password'}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Paper>

            {/* Preferences */}
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Palette color="primary" />
                <Typography variant="h6" fontWeight={700} color="text.primary">Preferences</Typography>
              </Box>

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">Dark Mode</Typography>
                    <Typography variant="caption" color="text.secondary">Switch between light and dark theme</Typography>
                  </Box>
                  <Switch
                    checked={mode === 'dark'}
                    onChange={toggleMode}
                    color="primary"
                  />
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
